import React from "react";
import {
  Modal,
  Box,
  Grid,
  FormControl,
  TextField,
  Button,
  IconButton,
  InputAdornment,
} from "@mui/material";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import ListSubheader from "@mui/material/ListSubheader";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import CloseIcon from "@mui/icons-material/Close";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Typography from "@mui/material/Typography";
import { useSelector } from "react-redux";
import { store } from "../../store";
import { showErrorAlert, showSuccessAlert } from "../ui/utils/AlertUtils";
import { createUser } from "../../redux/actions/user";
import { getStudies, getStudiesBySponsor } from "../../redux/actions/study";
import { getCenters } from "../../redux/actions/center";
import { getSponsors } from "../../redux/actions/sponsor";
import { generatePassword } from "../ui/utils/PasswordGenerator";
import { useTranslation } from "react-i18next";
import { instance } from "../../axios";

const formatFirstname = (value) =>
  value
    .toLowerCase()
    .replace(/(^|[\s-])(\S)/g, (match, prefix, letter) =>
      `${prefix}${letter.toUpperCase()}`,
    );

export const ModalUser = ({ visible, onClose }) => {
  const { t } = useTranslation();
  const [name, setName] = React.useState("");
  const [firstname, setFirstname] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [role, setRole] = React.useState("");
  const [organization, setOrganization] = React.useState("");
  const [phoneNumber, setPhoneNumber] = React.useState("");
  const studies = useSelector((state) => state.study.studies);
  const centers = useSelector((state) => state.center.centers);
  const sponsors = useSelector((state) => state.sponsor.sponsors);
  const [centerId, setCenterId] = React.useState("");
  const [studyId, setStudyId] = React.useState("");
  const [checkedStudies, setCheckedStudies] = React.useState([]);
  const [sponsorId, setSponsorId] = React.useState("");
  const [checkedCenters, setCheckedCenters] = React.useState([]);
  const [managerAssignments, setManagerAssignments] = React.useState([
    { studyId: "", centerIds: [] },
  ]);
  const [managerCentersByStudy, setManagerCentersByStudy] = React.useState({});
  const [errors, setErrors] = React.useState({});
  const [password, setPassword] = React.useState(generatePassword());
  const [showPassword, setShowPassword] = React.useState(false);
  const isLoading = useSelector((state) => state.user.isLoadingUser);

  const isCenterManager = organization === "Center" && role === "manager";
  const isSponsorMonitor = organization === "Sponsor" && role === "monitor";
  const isDynamicStudyCenter = isCenterManager || isSponsorMonitor;

  const sectionCardSx = React.useMemo(
    () => ({
      border: "1px solid",
      borderColor: "grey.100",
      borderRadius: 2,
      bgcolor: "grey.50",
      p: 2.5,
    }),
    [],
  );

  const placeholderTextSx = React.useMemo(
    () => ({
      color: "text.disabled",
      fontStyle: "italic",
    }),
    [],
  );

  const normalizedManagerAssignments = React.useMemo(
    () =>
      (managerAssignments || [])
        .filter((row) => row.studyId !== "" && row.studyId !== null)
        .map((row) => ({
          studyId: Number(row.studyId),
          centerIds: [
            ...new Set((row.centerIds || []).map((id) => Number(id))),
          ],
        })),
    [managerAssignments],
  );

  const managerStudyIds = React.useMemo(
    () => [...new Set(normalizedManagerAssignments.map((row) => row.studyId))],
    [normalizedManagerAssignments],
  );

  const managerCenterIds = React.useMemo(
    () => [
      ...new Set(
        normalizedManagerAssignments.flatMap((row) => row.centerIds || []),
      ),
    ],
    [normalizedManagerAssignments],
  );

  const resolveCenterCountry = React.useCallback((center) => {
    return (
      center?.country?.name ||
      center?.countryName ||
      center?.country ||
      center?.Country?.name ||
      ""
    );
  }, []);

  const buildCenterOptions = React.useCallback(
    (rowCenters, row, rowPrefix, rowIndex) => {
      const hasCountryInfo = (rowCenters || []).some((center) =>
        Boolean(resolveCenterCountry(center)),
      );

      if (!hasCountryInfo) {
        return (rowCenters || []).map((center) => (
          <MenuItem key={`${rowPrefix}-center-${center.id}`} value={center.id}>
            <Checkbox checked={(row.centerIds || []).includes(center.id)} />
            <span>{center.centerNumber}</span>
          </MenuItem>
        ));
      }

      const grouped = (rowCenters || []).reduce((acc, center) => {
        const country = resolveCenterCountry(center) || "Other";
        if (!acc[country]) {
          acc[country] = [];
        }
        acc[country].push(center);
        return acc;
      }, {});

      const groups = Object.entries(grouped);
      const items = [];

      groups.forEach(([country, centersInCountry], countryIndex) => {
        items.push(
          <ListSubheader
            key={`${rowPrefix}-country-${rowIndex}-${country}`}
            disableSticky>
            {country}
          </ListSubheader>,
        );

        centersInCountry.forEach((center) => {
          items.push(
            <MenuItem
              key={`${rowPrefix}-center-${country}-${center.id}`}
              value={center.id}>
              <Checkbox checked={(row.centerIds || []).includes(center.id)} />
              <span>{center.centerNumber}</span>
            </MenuItem>,
          );
        });

        if (countryIndex < groups.length - 1) {
          items.push(
            <Divider
              key={`${rowPrefix}-country-divider-${rowIndex}-${country}`}
            />,
          );
        }
      });

      return items;
    },
    [resolveCenterCountry],
  );

  const renderDynamicStudyCenterRows = React.useCallback(
    (rowPrefix) =>
      (managerAssignments || []).map((row, index) => {
        const currentStudyId = Number(row.studyId);
        const rowCenters = managerCentersByStudy[currentStudyId] || [];
        const hasSelectedCenters =
          Array.isArray(row.centerIds) && row.centerIds.length > 0;

        const selectedInOtherRows = (managerAssignments || [])
          .filter((_, rowIndex) => rowIndex !== index)
          .map((entry) => Number(entry.studyId))
          .filter((id) => Number.isFinite(id) && id > 0);

        return (
          <Box
            key={`${rowPrefix}-row-${index}`}
            sx={{
              position: "relative",
              border: "1px solid",
              borderColor: "grey.100",
              borderRadius: 2,
              px: 2,
              py: 1.25,
              bgcolor: "grey.50",
              mb: index === (managerAssignments?.length || 0) - 1 ? 0 : 1,
            }}>
            <IconButton
              size="small"
              color="error"
              disabled={(managerAssignments || []).length === 1}
              onClick={() => {
                setManagerAssignments((prev) => {
                  const next = (prev || []).filter(
                    (_, rowIndex) => rowIndex !== index,
                  );
                  return next.length > 0
                    ? next
                    : [{ studyId: "", centerIds: [] }];
                });
              }}
              sx={{
                position: "absolute",
                top: -10,
                right: -10,
                bgcolor: "rgba(211, 47, 47, 0.10)",
                color: "error.main",
                width: 24,
                height: 24,
                boxShadow: 1,
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "error.main",
                  color: "common.white",
                  transform: "scale(1.06)",
                },
                "&.Mui-disabled": {
                  bgcolor: "action.disabledBackground",
                  color: "action.disabled",
                },
              }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel id={`${rowPrefix}-study-${index}`}>
                    Study
                  </InputLabel>
                  <Select
                    labelId={`${rowPrefix}-study-${index}`}
                    value={row.studyId}
                    label="Study"
                    onChange={(event) => {
                      const nextStudyId = Number(event.target.value);
                      setManagerAssignments((prev) =>
                        (prev || []).map((entry, rowIndex) =>
                          rowIndex === index
                            ? {
                                studyId: nextStudyId,
                                centerIds: [],
                              }
                            : entry,
                        ),
                      );
                    }}>
                    {studies.map((study) => (
                      <MenuItem
                        key={`${rowPrefix}-study-opt-${study.id}`}
                        value={study.id}
                        disabled={selectedInOtherRows.includes(
                          Number(study.id),
                        )}>
                        {study.cdlStudyCode}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <FormControl
                  fullWidth
                  disabled={!row.studyId}
                  sx={{ position: "relative", pb: 0 }}>
                  {row.studyId && hasSelectedCenters ? (
                    <InputLabel id={`${rowPrefix}-centers-${index}`}>
                      Centers
                    </InputLabel>
                  ) : null}
                  <Select
                    labelId={
                      row.studyId && hasSelectedCenters
                        ? `${rowPrefix}-centers-${index}`
                        : undefined
                    }
                    multiple
                    displayEmpty
                    value={row.centerIds || []}
                    label={
                      row.studyId && hasSelectedCenters ? "Centers" : undefined
                    }
                    onChange={(event) => {
                      const value = event.target.value;
                      const nextCenterIds =
                        typeof value === "string"
                          ? value.split(",").map((id) => Number(id))
                          : value.map((id) => Number(id));

                      setManagerAssignments((prev) =>
                        (prev || []).map((entry, rowIndex) =>
                          rowIndex === index
                            ? {
                                ...entry,
                                centerIds: nextCenterIds,
                              }
                            : entry,
                        ),
                      );
                    }}
                    renderValue={(selected) =>
                      !row.studyId ? (
                        <Typography component="span" sx={placeholderTextSx}>
                          Please select a study first
                        </Typography>
                      ) : !selected || selected.length === 0 ? (
                        <Typography component="span" sx={placeholderTextSx}>
                          Centers
                        </Typography>
                      ) : (
                        (selected || [])
                          .map(
                            (id) =>
                              (rowCenters.find((c) => c.id === id) || {})
                                .centerNumber || id,
                          )
                          .join(", ")
                      )
                    }>
                    {buildCenterOptions(rowCenters, row, rowPrefix, index)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );
      }),
    [
      buildCenterOptions,
      managerAssignments,
      managerCentersByStudy,
      placeholderTextSx,
      studies,
    ],
  );

  const resetForm = React.useCallback(() => {
    setName("");
    setFirstname("");
    setEmail("");
    setPassword(generatePassword());
    setShowPassword(false);
    setRole("");
    setOrganization("");
    setPhoneNumber("");
    setSponsorId("");
    setStudyId("");
    setCenterId("");
    setCheckedCenters([]);
    setCheckedStudies([]);
    setManagerAssignments([{ studyId: "", centerIds: [] }]);
    setManagerCentersByStudy({});
    setErrors({});
  }, []);

  const handleCloseModal = React.useCallback(
    (created = false) => {
      resetForm();
      onClose(created);
    },
    [onClose, resetForm],
  );

  React.useEffect(() => {
    if (organization === "Center" && !role) {
      setRole("manager");
    }
  }, [organization, role]);

  const validateForm = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = t("nameRequired");
    else if (/[\d!@#$%^&*(),.?":{}|<>]/g.test(name))
      newErrors.name = t("nameInvalid");
    if (!firstname.trim()) newErrors.firstname = t("firstnameRequired");
    else if (/[\d!@#$%^&*(),.?":{}|<>]/g.test(firstname))
      newErrors.firstname = t("firstnameInvalid");
    if (!email.trim()) newErrors.email = t("emailRequired");
    if (!password.trim()) newErrors.password = t("passwordRequired");
    if (!role.trim()) newErrors.role = t("roleRequired");
    if (!organization.trim())
      newErrors.organization = t("organizationRequired");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    try {
      let nextCheckedStudies = checkedStudies;
      let nextCheckedCenters = checkedCenters;
      let nextCenterId = centerId;

      if (isDynamicStudyCenter) {
        nextCheckedStudies = managerStudyIds;
        nextCheckedCenters = managerCenterIds;
        nextCenterId = managerCenterIds[0] || null;
      } else if (organization === "Center") {
        const singleCenterId = centerId ? Number(centerId) : null;
        nextCheckedCenters = singleCenterId ? [singleCenterId] : [];
        nextCheckedStudies = studyId ? [Number(studyId)] : [];
        nextCenterId = singleCenterId;
      }

      const created = await store.dispatch(
        createUser({
          name,
          firstname,
          email,
          password,
          role,
          organization,
          phoneNumber,
          centerId: nextCenterId === "" ? null : nextCenterId,
          sponsorId: isSponsorMonitor
            ? null
            : sponsorId === ""
              ? null
              : sponsorId,
          checkedCenters: nextCheckedCenters,
          checkedStudies: nextCheckedStudies,
        }),
      );

      if (created?.success) {
        showSuccessAlert("The user was successfully created");
        handleCloseModal(true);
        return;
      }

      if (created?.status === 409 && created?.code === "EMAIL_ALREADY_EXISTS") {
        setErrors((prev) => ({
          ...prev,
          email: "email already existe",
        }));
        return;
      }

      showErrorAlert("An error occurred while creating the user");
    } catch (error) {
      console.error(error);
      showErrorAlert("An error occurred while creating the user");
    }
  };

  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

  const handleEmailChange = (event) => {
    const value = event.target.value.toLowerCase();
    setEmail(value);

    if (!emailRegex.test(value)) {
      setErrors((prev) => ({ ...prev, email: "Invalid email address" }));
    } else {
      setErrors((prev) => ({ ...prev, email: "" }));
    }
  };

  const handlePhoneNumberChange = (event) => {
    setPhoneNumber(event.target.value);

    const value = event.target.value;
    const phoneRegex = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/g;

    if (value && !phoneRegex.test(value)) {
      setErrors({
        ...errors,
        phoneNumber: "Invalid phone number, enter digits only",
      });
    } else {
      setErrors({ ...errors, phoneNumber: "" });
    }
  };

  React.useEffect(() => {
    if (!isCenterManager && studyId) {
      store.dispatch(getCenters(studyId));
    }
  }, [studyId, isCenterManager]);

  React.useEffect(() => {
    if (organization === "Sponsor" && role === "manager") {
      store.dispatch(getSponsors());
    }
  }, [organization, role]);

  React.useEffect(() => {
    if (isSponsorMonitor) {
      store.dispatch(getStudies());
      return;
    }

    if (sponsorId) {
      store.dispatch(getStudiesBySponsor(sponsorId));
    } else {
      store.dispatch(getStudies());
    }
  }, [sponsorId, isSponsorMonitor]);

  React.useEffect(() => {
    if (!visible) return;
    if (!isDynamicStudyCenter) return;

    const selectedStudyIds = managerStudyIds;
    const missingStudyIds = selectedStudyIds.filter(
      (id) => !managerCentersByStudy[id],
    );

    if (missingStudyIds.length === 0) return;

    let isCancelled = false;

    const fetchCentersForStudies = async () => {
      try {
        const responses = await Promise.all(
          missingStudyIds.map(async (id) => {
            const response = await instance.get(`/center/study/${id}`);
            return { studyId: id, centers: response?.data || [] };
          }),
        );

        if (isCancelled) return;

        setManagerCentersByStudy((prev) => {
          const next = { ...prev };
          responses.forEach(({ studyId: currentStudyId, centers: rows }) => {
            next[currentStudyId] = rows.map((center) => ({
              ...center,
              studyId: center.studyId || currentStudyId,
            }));
          });
          return next;
        });
      } catch (error) {
        if (!isCancelled) {
          setManagerCentersByStudy((prev) => ({ ...prev }));
        }
      }
    };

    fetchCentersForStudies();

    return () => {
      isCancelled = true;
    };
  }, [visible, isDynamicStudyCenter, managerStudyIds, managerCentersByStudy]);

  React.useEffect(() => {
    if (!isDynamicStudyCenter) return;
    setCheckedStudies(managerStudyIds);
    setCheckedCenters(managerCenterIds);
    setCenterId(managerCenterIds[0] || "");
  }, [isDynamicStudyCenter, managerStudyIds, managerCenterIds]);

  React.useEffect(() => {
    if (!visible) {
      resetForm();
    }
  }, [visible, resetForm]);

  return (
    <Modal open={visible} onClose={() => handleCloseModal(false)}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          border: "1px solid rgba(211,47,47,0.18)",
          borderRadius: 2,
          width: 800,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          "& .MuiInputBase-input::placeholder": {
            fontStyle: "italic",
            opacity: 1,
          },
          "& textarea::placeholder": {
            fontStyle: "italic",
            opacity: 1,
          },
        }}
        component="form"
        noValidate
        onSubmit={handleSubmit}>
        <IconButton
          style={{
            position: "absolute",
            top: 10,
            right: 10,
          }}
          onClick={() => {
            handleCloseModal(false);
          }}>
          <CloseIcon />
        </IconButton>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" component="h2">
              {t("createNewUser")}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box sx={sectionCardSx}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <TextField
                      required
                      name="input-name"
                      value={name}
                      onChange={(event) => {
                        setName(event.target.value.toUpperCase());
                      }}
                      id="input-name"
                      label={t("name")}
                      error={!!errors.name}
                      helperText={errors.name}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <TextField
                      required
                      name="input-firstname"
                      value={firstname}
                      onChange={(event) => {
                        setFirstname(formatFirstname(event.target.value));
                      }}
                      id="input-firstname"
                      label={t("firstname")}
                      error={!!errors.firstname}
                      helperText={errors.firstname}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <TextField
                      required
                      name="input-email"
                      value={email}
                      onChange={handleEmailChange}
                      id="input-email"
                      label={t("email")}
                      error={!!errors.email}
                      helperText={errors.email}
                      sx={{
                        "& .MuiInputBase-root": {
                          bgcolor: errors.email
                            ? "rgba(244, 67, 54, 0.10)"
                            : "",
                        },
                      }}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <TextField
                      name="input-phoneNumber"
                      value={phoneNumber}
                      onChange={handlePhoneNumberChange}
                      id="input-phoneNumber"
                      label={t("phoneNumber")}
                      error={!!errors.phoneNumber}
                      helperText={errors.phoneNumber}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <TextField
                      autoComplete="new-password"
                      required
                      name="input-password"
                      value={password}
                      onChange={(event) => {
                        setPassword(event.target.value);
                      }}
                      id="input-password"
                      label={t("password")}
                      type={showPassword ? "text" : "password"}
                      error={!!errors.password}
                      helperText={errors.password}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showPassword ? "hide password" : "show password"
                              }
                              onClick={() => setShowPassword((prev) => !prev)}
                              edge="end">
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>

                <Grid
                  item
                  xs={6}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                  }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    sx={{ height: 56 }}
                    onClick={() => setPassword(generatePassword())}>
                    {t("generatePassword")}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={sectionCardSx}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth error={!!errors.organization}>
                    <InputLabel id="organization-select-label">
                      {t("organization")} *
                    </InputLabel>
                    <Select
                      required
                      name="input-organization"
                      value={organization}
                      onChange={(event) => {
                        setOrganization(event.target.value);
                        setRole(
                          event.target.value === "Center" ? "manager" : "",
                        );
                        setStudyId("");
                        setCheckedCenters([]);
                        setCheckedStudies([]);
                        setManagerAssignments([{ studyId: "", centerIds: [] }]);
                        setManagerCentersByStudy({});
                        setCenterId("");
                        setSponsorId("");
                      }}
                      id="input-organization"
                      label="Organization">
                      <MenuItem value="CDL">{t("cdl")}</MenuItem>
                      <MenuItem value="Sponsor">{t("sponsor")}</MenuItem>
                      <MenuItem value="Center">{t("center")}</MenuItem>
                    </Select>
                    {errors.organization && (
                      <FormHelperText>{errors.organization}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth error={!!errors.role}>
                    <InputLabel id="role-select-label">Role *</InputLabel>
                    <Select
                      required
                      disabled={organization === "Center"}
                      name="input-role"
                      value={role}
                      onChange={(event) => {
                        setRole(event.target.value);
                      }}
                      id="input-role"
                      label={t("role")}>
                      {organization === "CDL"
                        ? [
                            <MenuItem key="admin" value="admin">
                              {t("admin")}
                            </MenuItem>,
                            <MenuItem key="technician" value="technician">
                              {t("technician")}
                            </MenuItem>,
                            <MenuItem key="employee" value="employee">
                              {t("employee")}
                            </MenuItem>,
                          ]
                        : organization === "Center"
                          ? [
                              <MenuItem key="manager" value="manager">
                                {t("manager")}
                              </MenuItem>,
                            ]
                          : [
                              <MenuItem key="manager" value="manager">
                                {t("manager")}
                              </MenuItem>,
                              <MenuItem key="monitor" value="monitor">
                                {t("monitor")}
                              </MenuItem>,
                            ]}
                    </Select>
                    {errors.role && (
                      <FormHelperText>{errors.role}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {isDynamicStudyCenter && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        width: "100%",
                      }}>
                      <Button
                        variant="outlined"
                        sx={{
                          height: 56,
                          minHeight: 56,
                          px: 3,
                        }}
                        onClick={() =>
                          setManagerAssignments((prev) => [
                            ...(prev || []),
                            { studyId: "", centerIds: [] },
                          ])
                        }>
                        + Add Study & Center
                      </Button>
                    </Box>
                  </Grid>
                )}

                {organization === "Sponsor" && role !== "monitor" && (
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel id="sponsor-select-label">
                        {t("sponsor")}
                      </InputLabel>
                      <Select
                        required
                        name="input-sponsorId"
                        value={sponsorId}
                        onChange={(event) => {
                          setSponsorId(event.target.value);
                          setStudyId("");
                          setCheckedCenters([]);
                          setManagerAssignments([
                            { studyId: "", centerIds: [] },
                          ]);
                          setManagerCentersByStudy({});
                        }}
                        id="input-sponsorId"
                        label={t("sponsor")}>
                        {Array.isArray(sponsors)
                          ? sponsors.map((sponsor) => (
                              <MenuItem key={sponsor.id} value={sponsor.id}>
                                {sponsor.name}
                              </MenuItem>
                            ))
                          : null}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {organization === "Sponsor" &&
                  role === "manager" &&
                  sponsorId && (
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel id="sponsor-manager-studies-multi">
                          {t("studies")}
                        </InputLabel>
                        <Select
                          labelId="sponsor-manager-studies-multi"
                          multiple
                          value={checkedStudies}
                          label={t("studies")}
                          onChange={(event) => {
                            const value = event.target.value;
                            setCheckedStudies(
                              typeof value === "string"
                                ? value.split(",").map((id) => Number(id))
                                : value.map((id) => Number(id)),
                            );
                          }}
                          renderValue={(selected) =>
                            (selected || [])
                              .map(
                                (id) =>
                                  (
                                    studies.find((study) => study.id === id) ||
                                    {}
                                  ).cdlStudyCode || id,
                              )
                              .join(", ")
                          }>
                          {studies.map((study) => (
                            <MenuItem key={study.id} value={study.id}>
                              <Checkbox
                                checked={checkedStudies.includes(study.id)}
                              />
                              <span>{study.cdlStudyCode}</span>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                {organization === "CDL" && role === "employee" && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="cdl-employee-studies-multi">
                        {t("studies")}
                      </InputLabel>
                      <Select
                        labelId="cdl-employee-studies-multi"
                        multiple
                        value={checkedStudies}
                        label={t("studies")}
                        onChange={(event) => {
                          const value = event.target.value;
                          setCheckedStudies(
                            typeof value === "string"
                              ? value.split(",").map((id) => Number(id))
                              : value.map((id) => Number(id)),
                          );
                        }}
                        renderValue={(selected) =>
                          (selected || [])
                            .map(
                              (id) =>
                                (studies.find((study) => study.id === id) || {})
                                  .cdlStudyCode || id,
                            )
                            .join(", ")
                        }>
                        {studies.map((study) => (
                          <MenuItem key={study.id} value={study.id}>
                            <Checkbox
                              checked={checkedStudies.includes(study.id)}
                            />
                            <span>{study.cdlStudyCode}</span>
                          </MenuItem>
                        ))}
                      </Select>
                      {!studies || studies.length === 0 ? (
                        <FormHelperText>{t("noStudies")}</FormHelperText>
                      ) : null}
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>

          {organization === "Center" && (
            <>
              {isCenterManager ? (
                <Grid item xs={12}>
                  {renderDynamicStudyCenterRows("dynamic")}
                </Grid>
              ) : (
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel id="study-select-label">
                      {t("study")}
                    </InputLabel>
                    <Select
                      required
                      name="input-studyId"
                      value={studyId}
                      onChange={(event) => {
                        const selectedStudy = studies.find(
                          (study) => study.id === event.target.value,
                        );
                        setStudyId(selectedStudy?.id || "");
                        setCenterId("");
                      }}
                      id="input-studyId"
                      label={t("study")}>
                      {studies.map((study) => (
                        <MenuItem key={study.id} value={study.id}>
                          {study.cdlStudyCode}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {!isCenterManager && studyId && (
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    {centers.length > 0 ? (
                      <>
                        <InputLabel id="center-select-label">
                          {t("centerNumber")}
                        </InputLabel>
                        <Select
                          required
                          name="input-centerId"
                          value={centerId}
                          onChange={(event) => {
                            const selectedCenter = centers.find(
                              (center) => center.id === event.target.value,
                            );
                            setCenterId(selectedCenter?.id || "");
                          }}
                          id="input-centerId"
                          label={t("centerNumber")}>
                          {centers.map((center) => (
                            <MenuItem key={center.id} value={center.id}>
                              {center.centerNumber}
                            </MenuItem>
                          ))}
                        </Select>
                      </>
                    ) : (
                      <Box mt={2}>
                        <Typography variant="body1">
                          {t("noCentersForStudy")}
                        </Typography>
                      </Box>
                    )}
                  </FormControl>
                </Grid>
              )}
            </>
          )}

          {organization === "Sponsor" && role === "monitor" && (
            <Grid item xs={12}>
              {renderDynamicStudyCenterRows("dynamic")}
            </Grid>
          )}

          {!isSponsorMonitor && role === "monitor" && (
            <>
              <Grid item xs={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="study-label">{t("study")}</InputLabel>
                  <Select
                    labelId="study-label"
                    id="study-select"
                    value={studyId}
                    onChange={(event) => {
                      setStudyId(event.target.value);
                      setCheckedCenters([]);
                    }}
                    label={t("study")}>
                    {studies &&
                      studies.map((study) => (
                        <MenuItem key={study.id} value={study.id}>
                          {study.cdlStudyCode}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              {studyId && (
                <Grid item xs={12}>
                  <FormControl fullWidth disabled={!studyId}>
                    <InputLabel id="monitor-centers-multi">
                      {t("centers")}
                    </InputLabel>
                    <Select
                      labelId="monitor-centers-multi"
                      multiple
                      value={checkedCenters}
                      label={t("centers")}
                      onChange={(event) => {
                        const value = event.target.value;
                        setCheckedCenters(
                          typeof value === "string"
                            ? value.split(",").map((id) => Number(id))
                            : value.map((id) => Number(id)),
                        );
                      }}
                      renderValue={(selected) =>
                        (selected || [])
                          .map(
                            (id) =>
                              (centers.find((center) => center.id === id) || {})
                                .centerNumber || id,
                          )
                          .join(", ")
                      }>
                      {centers.map((center) => (
                        <MenuItem key={center.id} value={center.id}>
                          <Checkbox
                            checked={checkedCenters.includes(center.id)}
                          />
                          <span>{center.centerNumber}</span>
                        </MenuItem>
                      ))}
                    </Select>
                    {!studyId ? (
                      <FormHelperText>{t("selectStudyFirst")}</FormHelperText>
                    ) : !centers || centers.length === 0 ? (
                      <FormHelperText>{t("noCenters")}</FormHelperText>
                    ) : null}
                  </FormControl>
                </Grid>
              )}
            </>
          )}

          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={isLoading}
              sx={{ mt: 2, mb: 1, padding: 2 }}>
              {t("create")}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};
